import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, InputGroup, Spinner } from "react-bootstrap";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { TopographNavbar } from "../components/TopographNavbar";
import { MapOverview } from "../components/MapOverview";
import { TopographStlCarousel } from "../components/TopographStlCarousel";
import { useAuth } from "../context/AuthContext";
import { CLIENT } from "../lib/api";
import { stripePromise } from "../lib/stripe";
import type { Discount, Map, Order, PostalAddress } from "../lib/types";
import {
    discountCentsFor,
    discountLabel,
    estimateFramePieces,
    getPrice,
    getRenderPieces,
    leadTime,
    PRICE_HANGERS,
    PRICE_PER_FRAME_SEGMENT,
    PRICE_PER_TILE,
    PRICE_PER_VENEER,
} from "../lib/pricing";
import { fmtS } from "../lib/units";

const dollarFmt = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});
const dollars = (cents: number) => dollarFmt.format(cents / 100);

function getParam(name: string): string | null {
    return new URLSearchParams(window.location.search).get(name);
}

interface AddressFields {
    name: string;
    line1: string;
    line2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
}

const EMPTY_ADDRESS: AddressFields = {
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    country: "US",
};

function toPostalAddress(a: AddressFields): PostalAddress {
    return {
        recipients: [a.name],
        addressLines: [a.line1, ...(a.line2 ? [a.line2] : [])],
        locality: a.city,
        administrativeArea: a.state,
        postalCode: a.zip,
        regionCode: a.country,
    };
}

// PaymentForm renders inside <Elements> (it needs the hook context).
function PaymentForm({
    amountCents,
    onSuccess,
    onCancel,
}: {
    amountCents: number;
    onSuccess: () => void;
    onCancel: () => void;
}) {
    const stripe = useStripe();
    const elements = useElements();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const pay = async () => {
        if (!stripe || !elements) return;
        setBusy(true);
        setError(null);
        // Card payments confirm inline; redirect-based methods bounce
        // through Stripe and land on /orders, where the webhook-updated
        // payment status is visible.
        const result = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: `${window.location.origin}/orders` },
            redirect: "if_required",
        });
        if (result.error) {
            setError(result.error.message ?? "Payment failed.");
            setBusy(false);
            return;
        }
        onSuccess();
    };

    return (
        <div>
            <PaymentElement />
            {error && (
                <Alert variant="danger" style={{ marginTop: "0.75rem" }}>
                    {error}
                </Alert>
            )}
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
                <Button variant="warning" disabled={busy || !stripe} onClick={pay}>
                    {busy ? <Spinner size="sm" /> : <i className="bi bi-lock" />}
                    &nbsp;Pay {dollars(amountCents)}
                </Button>
                <Button variant="outline-secondary" disabled={busy} onClick={onCancel}>
                    Cancel order
                </Button>
            </div>
        </div>
    );
}

export function Checkout() {
    const auth = useAuth();
    const [map, setMap] = useState<Map | null>(null);
    // The file key currently shown in the STL viewer. Shared between the map
    // overview (click a tile to select it) and the carousel (nav syncs back).
    const [selectedFile, setSelectedFile] = useState<string | null>(null);
    const mapId = getParam("mapId");
    const resumeOrderId = getParam("orderId");

    // Checkout progression: fill the form → pay the PaymentIntent → done.
    const [phase, setPhase] = useState<"form" | "pay" | "done">("form");
    const [order, setOrder] = useState<Order | null>(null);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [placing, setPlacing] = useState(false);

    const [address, setAddress] = useState<AddressFields>(EMPTY_ADDRESS);

    // Coupon entry: `applied` is the server-validated discount.
    const [couponInput, setCouponInput] = useState("");
    const [coupon, setCoupon] = useState<Discount | null>(null);
    const [couponError, setCouponError] = useState<string | null>(null);
    const [checkingCoupon, setCheckingCoupon] = useState(false);

    useEffect(() => {
        if (!auth.loggedIn || !mapId) return;
        let cancelled = false;
        (async () => {
            try {
                const m = await CLIENT.getMap(mapId);
                if (!cancelled) setMap(m);
            } catch (err) {
                console.error("fetchMap failed:", err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [auth.loggedIn, mapId]);

    // Resume an existing unpaid order (linked from the Orders page).
    useEffect(() => {
        if (!auth.loggedIn || !resumeOrderId) return;
        let cancelled = false;
        (async () => {
            try {
                const resp = await CLIENT.payOrder(resumeOrderId);
                if (cancelled) return;
                setOrder(resp.order);
                setClientSecret(resp.stripeClientSecret);
                setPhase("pay");
            } catch (err) {
                if (!cancelled) setError(`Cannot resume payment: ${err}`);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [auth.loggedIn, resumeOrderId]);

    const applyCoupon = useCallback(async () => {
        const code = couponInput.trim();
        if (!code) return;
        setCheckingCoupon(true);
        setCouponError(null);
        try {
            const d = await CLIENT.checkDiscount(code);
            if (!d.usable) {
                setCoupon(null);
                setCouponError(d.reason ?? "This code cannot be used.");
            } else {
                setCoupon(d);
            }
        } catch {
            setCoupon(null);
            setCouponError("Unknown coupon code.");
        } finally {
            setCheckingCoupon(false);
        }
    }, [couponInput]);

    const placeOrder = async () => {
        if (!map) return;
        for (const [field, label] of [
            [address.name, "name"],
            [address.line1, "address"],
            [address.city, "city"],
            [address.state, "state"],
            [address.zip, "postal code"],
        ] as const) {
            if (!field.trim()) {
                setError(`Please fill in your ${label}.`);
                return;
            }
        }
        setPlacing(true);
        setError(null);
        try {
            const postal = toPostalAddress(address);
            const resp = await CLIENT.createOrder({
                mapId: map.id,
                couponCode: coupon?.id,
                shippingAddress: postal,
                billingAddress: postal,
            });
            setOrder(resp.order);
            if (resp.stripeClientSecret) {
                setClientSecret(resp.stripeClientSecret);
                setPhase("pay");
            } else {
                // Fully comped by the coupon — nothing to pay.
                setPhase("done");
            }
        } catch (err) {
            setError(`${err}`);
        } finally {
            setPlacing(false);
        }
    };

    const cancelOrder = async () => {
        if (!order) return;
        try {
            await CLIENT.cancelOrder(order.id);
        } catch (err) {
            console.error("cancelOrder failed:", err);
        }
        setOrder(null);
        setClientSecret(null);
        setPhase("form");
    };

    if (!auth.loggedIn) {
        return (
            <>
                <TopographNavbar />
                <br />
                <div style={{ marginLeft: "1rem" }}>You are not logged in! Log in to see your maps.</div>
            </>
        );
    }

    if (!map && !resumeOrderId) {
        return (
            <>
                <TopographNavbar />
                <br />
                <div style={{ marginLeft: "1rem" }}>Loading map...</div>
            </>
        );
    }

    const rendered = map !== null && (map.status === "rendered" || map.status === "framed");
    const subtotalCents = map ? Math.round(getPrice(map) * 100) : 0;
    const discountCents = coupon && map ? discountCentsFor(coupon, subtotalCents) : 0;
    const totalCents = subtotalCents - discountCents;

    return (
        <>
            <TopographNavbar />
            <br />
            <div style={{ width: "100vw", display: "flex", flexDirection: "row" }}>
                <div style={{ width: "100%" }}>
                    {rendered && (
                        <TopographStlCarousel
                            width="100%"
                            height="95vh"
                            map={map}
                            selectedKey={selectedFile}
                            onSelect={setSelectedFile}
                        />
                    )}
                </div>
                <div style={{ width: "38rem", marginLeft: "1rem", marginRight: "1rem" }}>
                    <h5>Order Form</h5>
                    <hr />
                    {map && (
                        <>
                            <MapOverview
                                map={map}
                                selectedKey={selectedFile}
                                onTileClick={setSelectedFile}
                            />
                            <hr />
                        </>
                    )}
                    {error && <Alert variant="danger">{error}</Alert>}

                    {phase === "done" && order && (
                        <Alert variant="success">
                            <Alert.Heading as="h6">
                                <i className="bi bi-check-circle" /> Order placed
                            </Alert.Heading>
                            {order.paymentStatus === "free" ? (
                                <>Your order is fully covered by the coupon — nothing to pay.</>
                            ) : (
                                <>Payment received. We&apos;ll get printing.</>
                            )}
                            <br />
                            Track it on the <a href="/orders">Orders</a> page.
                        </Alert>
                    )}

                    {phase === "pay" && clientSecret && order && (
                        <>
                            <h6>Payment — {dollars(order.amountCents)}</h6>
                            {order.discountCents ? (
                                <div style={{ color: "#999", marginBottom: "0.5rem" }}>
                                    includes −{dollars(order.discountCents)} coupon{" "}
                                    <code>{order.couponCode}</code>
                                </div>
                            ) : null}
                            <Elements
                                stripe={stripePromise}
                                options={{
                                    clientSecret,
                                    appearance: {
                                        theme: "night",
                                        variables: { colorPrimary: "#FFBF00" },
                                    },
                                }}
                            >
                                <PaymentForm
                                    amountCents={order.amountCents}
                                    onSuccess={() => setPhase("done")}
                                    onCancel={cancelOrder}
                                />
                            </Elements>
                        </>
                    )}

                    {phase === "form" && map && rendered && (
                        <>
                            <table style={{ width: "100%" }}>
                                <tbody>
                                    <tr>
                                        <th>Item</th>
                                        <th>Qty</th>
                                        <th>Price</th>
                                    </tr>
                                    <tr>
                                        <td>Map Tile</td>
                                        <td>{getRenderPieces(map.files).length}</td>
                                        <td>{dollarFmt.format(getRenderPieces(map.files).length * PRICE_PER_TILE)}</td>
                                    </tr>
                                    <tr>
                                        <td>Frame Segment</td>
                                        <td>{estimateFramePieces(map)}</td>
                                        <td>{dollarFmt.format(estimateFramePieces(map) * PRICE_PER_FRAME_SEGMENT)}</td>
                                    </tr>
                                    <tr>
                                        <td>Veneer</td>
                                        <td>8</td>
                                        <td>{dollarFmt.format(8 * PRICE_PER_VENEER)}</td>
                                    </tr>
                                    <tr>
                                        <td>Hanging Equipment</td>
                                        <td>1</td>
                                        <td>{dollarFmt.format(PRICE_HANGERS)}</td>
                                    </tr>
                                    {map.status === "framed" && (
                                        <tr style={{ color: "lime" }}>
                                            <td>(unlocked download)</td>
                                            <td>1</td>
                                            <td>-$10.00</td>
                                        </tr>
                                    )}
                                    {coupon && (
                                        <tr style={{ color: "lime" }}>
                                            <td>
                                                Coupon <code>{coupon.id}</code> ({discountLabel(coupon)}){" "}
                                                <a
                                                    href="#"
                                                    style={{ fontSize: "0.85em" }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        setCoupon(null);
                                                        setCouponInput("");
                                                    }}
                                                >
                                                    remove
                                                </a>
                                            </td>
                                            <td>1</td>
                                            <td>−{dollars(discountCents)}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <td></td>
                                        <td><hr /></td>
                                        <td><hr /></td>
                                    </tr>
                                    <tr>
                                        <td><b>Total (excl. shipping)</b></td>
                                        <td></td>
                                        <td><b>{dollars(totalCents)}</b></td>
                                    </tr>
                                </tbody>
                            </table>

                            {!coupon && (
                                <div style={{ marginTop: "0.75rem" }}>
                                    <InputGroup size="sm" style={{ maxWidth: "20rem" }}>
                                        <Form.Control
                                            placeholder="coupon code"
                                            value={couponInput}
                                            onChange={(e) => setCouponInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") applyCoupon();
                                            }}
                                        />
                                        <Button
                                            variant="outline-warning"
                                            disabled={checkingCoupon || !couponInput.trim()}
                                            onClick={applyCoupon}
                                        >
                                            {checkingCoupon ? <Spinner size="sm" /> : "Apply"}
                                        </Button>
                                    </InputGroup>
                                    {couponError && (
                                        <div style={{ color: "#dc3545", fontSize: "0.85em", marginTop: "0.25rem" }}>
                                            {couponError}
                                        </div>
                                    )}
                                </div>
                            )}

                            <hr />
                            <h6>Shipping Address</h6>
                            <Form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    placeOrder();
                                }}
                            >
                                <Form.Control
                                    size="sm"
                                    className="mb-2"
                                    placeholder="Full name"
                                    autoComplete="name"
                                    value={address.name}
                                    onChange={(e) => setAddress({ ...address, name: e.target.value })}
                                />
                                <Form.Control
                                    size="sm"
                                    className="mb-2"
                                    placeholder="Address line 1"
                                    autoComplete="address-line1"
                                    value={address.line1}
                                    onChange={(e) => setAddress({ ...address, line1: e.target.value })}
                                />
                                <Form.Control
                                    size="sm"
                                    className="mb-2"
                                    placeholder="Address line 2 (optional)"
                                    autoComplete="address-line2"
                                    value={address.line2}
                                    onChange={(e) => setAddress({ ...address, line2: e.target.value })}
                                />
                                <div style={{ display: "flex", gap: "0.5rem" }} className="mb-2">
                                    <Form.Control
                                        size="sm"
                                        placeholder="City"
                                        autoComplete="address-level2"
                                        value={address.city}
                                        onChange={(e) => setAddress({ ...address, city: e.target.value })}
                                    />
                                    <Form.Control
                                        size="sm"
                                        placeholder="State"
                                        autoComplete="address-level1"
                                        style={{ maxWidth: "6rem" }}
                                        value={address.state}
                                        onChange={(e) => setAddress({ ...address, state: e.target.value })}
                                    />
                                    <Form.Control
                                        size="sm"
                                        placeholder="ZIP"
                                        autoComplete="postal-code"
                                        style={{ maxWidth: "7rem" }}
                                        value={address.zip}
                                        onChange={(e) => setAddress({ ...address, zip: e.target.value })}
                                    />
                                </div>
                                <Form.Select
                                    size="sm"
                                    className="mb-2"
                                    style={{ maxWidth: "12rem" }}
                                    value={address.country}
                                    onChange={(e) => setAddress({ ...address, country: e.target.value })}
                                >
                                    <option value="US">United States</option>
                                    <option value="CA">Canada</option>
                                </Form.Select>

                                <hr />
                                <div>
                                    This order will take <b>{fmtS(leadTime(map))}</b> to print.
                                    <br />
                                    <br />
                                    <Button variant="warning" type="submit" disabled={placing}>
                                        {placing ? <Spinner size="sm" /> : <i className="bi bi-bag" />}
                                        &nbsp;
                                        {totalCents === 0
                                            ? "Place free order"
                                            : `Continue to payment — ${dollars(totalCents)}`}
                                    </Button>
                                </div>
                            </Form>
                        </>
                    )}

                    {phase === "form" && map && !rendered && (
                        <div>Map is not yet ready to view. Try again later.</div>
                    )}
                </div>
            </div>
        </>
    );
}
